import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Formik } from 'formik';
import * as Yup from 'yup';

import { useInjection } from '@context/inversify-context-provider';
import { ImagePreview } from '@features/oneOnboarding/components/ImagePreview';
import { ReadOnlyDocumentInfo } from '@features/oneOnboarding/components/ReadOnlyDocumentInfo';
import { OODocumentContext } from '@features/oneOnboarding/context/DocumentContext';
import { OOFormControl } from '@features/oneOnboarding/controls/FormControl';
import { controlModelToValidationObject, parseValidationErrors } from '@features/oneOnboarding/helpers';
import { DOCUMENT_REJECTION_REASON, OOStepsEnum } from '@features/oneOnboarding/interfaces';
import { OOControlModel } from '@features/oneOnboarding/models/ControlModel';
import { OODocumentPartModel } from '@features/oneOnboarding/models/DocumentPartModel';
import { OOFlowWrapper } from '@features/oneOnboarding/wrappers/FlowWrapper';
import { ChosenFileValidation, validateChosenFile } from '@helpers/fileUpload.helper';
import { OnboardingDocumentStatus } from '@helpers/OnboardingSteps';
import { mutateUserFlow, useDownloadDocumentWithFullResponse, useUploadLimitSize } from '@hooks/apiHooks';
import { useDecodedParams } from '@hooks/useDecodedParams';
import { TYPES } from '@libs/InversifyType';
import { OnboardingStatus } from '@models/onboarding.model';
import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import { store } from '@store/index';
import { RootState } from '@store/rootReducer';
import { useQueryClient } from '@tanstack/react-query';

import { AamBackendApi } from '../../../../../../libs/aamBackendApi';

import PreviewActions from './components/PreviewActions';
import { FileUpload } from './FileUpload';

import styles from './DocumentPages.module.scss';

interface PreviewDocumentProps {
  changeToNextDocument: () => void;
  hasValidationStep: boolean;
}

interface State {
  [key: string]: {
    file?: File;
    data?: string | ArrayBuffer | null;
  };
}
const PreviewDocument: React.FC<PreviewDocumentProps> = ({ changeToNextDocument, hasValidationStep }) => {
  const [approvalInProgress, setApprovalInProgress] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<any | null>(null);
  const [selectedDocumentPartLabel, setSelectedDocumentPartLabel] = useState<string>('');
  const [imageControls, setImageControls] = useState({
    zoom: 0,
    rotate: 0,
    x: 0,
    y: 0,
  });
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [part, setPart] = useState<OODocumentPartModel>();
  const [, setTypeError] = useState<boolean>(false);
  const [, setSizeError] = useState<boolean>(false);
  const [, setEmptyFileError] = useState<boolean>(false);
  const [state, setState] = useState<State>({});

  let documentAction: OnboardingDocumentStatus = OnboardingDocumentStatus.VERIFIED;
  const { clientOrganizationId, userId, applicationId, configurationId } = useDecodedParams();
  const { t } = useTranslation(['documents', 'candidate_recruiter']);
  const divRef: any = useRef(null);
  const { tenantId } = useSelector((state: RootState) => state.tenant);
  const aamBackendApi = useInjection<AamBackendApi>(TYPES.AamBackendApi);
  const { step, document, setCommand, setDocument, documentationModel } = useContext(OODocumentContext);
  const queryClient = useQueryClient();

  const oneOnboarding = useSelector((state: RootState) => state.oneOnboarding);
  const { themeAdditionalData, selectedConfigurationId } = oneOnboarding;
  const { data: uploadFileSizeInfo } = useUploadLimitSize(clientOrganizationId, selectedConfigurationId!);

  const stepName = step?.name;
  const selectedDocument = document;
  const rejectionReasonControl = selectedDocument?.controls?.find(
    (control) => control.name === DOCUMENT_REJECTION_REASON,
  );

  const { selectedOnboarding } = useSelector((state: RootState) => state.dashboard)!;

  const documentImages = useDownloadDocumentWithFullResponse(tenantId, document?.parts || []);
  const images = useMemo(() => documentImages.filter((i) => i.data).map((image) => image.data) || [], [documentImages]);

  const flowData = store.getState().oneOnboarding.flow;

  const flowWrapper = useMemo(() => {
    if (!flowData) return null;
    return OOFlowWrapper.create(flowData);
  }, [flowData]);

  useEffect(() => {
    setSelectedImage(null);
  }, [selectedDocument]);

  const autoVerifyDocument = useCallback(async () => {
    const doc = selectedDocument?.userDocument;
    const isAlreadyVerified = doc?.additionalData?.status === OnboardingDocumentStatus.VERIFIED;

    if (!doc || hasValidationStep || isAlreadyVerified) return;

    await aamBackendApi.updateDocument(tenantId, doc.id, {
      additionalData: { status: OnboardingDocumentStatus.VERIFIED },
    });
    queryClient.invalidateQueries({
      queryKey: ['getUserFlow', clientOrganizationId, configurationId, userId, applicationId],
    });
  }, [
    tenantId,
    selectedDocument,
    hasValidationStep,
    aamBackendApi,
    queryClient,
    clientOrganizationId,
    configurationId,
    userId,
    applicationId,
  ]);

  useEffect(() => {
    if (!selectedDocument || !selectedImage) return;
    const part = selectedDocument.parts?.find((item) => item.name === selectedImage.name);
    setSelectedDocumentPartLabel(part?.label ?? selectedDocument.label);
    autoVerifyDocument();
  }, [selectedDocument, selectedImage, autoVerifyDocument]);

  useEffect(() => {
    if (!images[0] || selectedImage) return;

    setSelectedImage(images[0]);
  }, [images, selectedImage]);

  // Used to center image on load
  useEffect(() => {
    if (divRef.current && selectedImage) {
      const { width, height } = divRef.current.getBoundingClientRect();

      setImageControls((state: any) => ({ ...state, x: width / 2, y: height / 2 }));
    }
  }, [selectedImage, images.length]);

  const currentActiveImageIndex = useMemo(() => {
    return images.findIndex((i) => i?.name === selectedImage?.name) + 1;
  }, [images, selectedImage]);

  const validationSchema = useMemo(() => {
    if (!selectedDocument) return Yup.object({});
    return Yup.object({
      ...controlModelToValidationObject(selectedDocument?.controls, t, false, flowWrapper, 'documents'),
    });
  }, [selectedDocument, t, flowWrapper]);

  const initialValues = useMemo(() => {
    return selectedDocument?.controls
      ? selectedDocument.controls.reduce((acc: any, c: any) => {
          acc[c.name] = c.value;
          return acc;
        }, {} as any)
      : [];
  }, [selectedDocument?.controls]);

  const { mutateAsync } = mutateUserFlow(clientOrganizationId, configurationId, userId, applicationId, queryClient);

  if (!selectedDocument) {
    setCommand(undefined);
    setDocument(undefined);
    return <div>No document selected</div>;
  }

  const setStatusEx = async (status: OnboardingDocumentStatus) => {
    try {
      setApprovalInProgress(true);
      if (status === OnboardingDocumentStatus.REJECTED) {
        await aamBackendApi.rejectDocument(
          clientOrganizationId,
          userId,
          selectedDocument.userDocument.id,
          {
            additionalData: { status },
          },
          applicationId,
          configurationId,
        );
      } else {
        await aamBackendApi.approveDocument(
          clientOrganizationId,
          userId,
          selectedDocument.userDocument.id,
          {
            additionalData: { status },
          },
          applicationId,
          configurationId,
        );
      }
    } finally {
      setApprovalInProgress(false);
      setImageControls({ zoom: 0, rotate: 0, x: 0, y: 0 });
    }

    if (status !== OnboardingDocumentStatus.MISSING) {
      queryClient.invalidateQueries({
        queryKey: ['getUserFlow', clientOrganizationId, configurationId, userId, applicationId],
      });
      queryClient.invalidateQueries({
        queryKey: ['getUserOOFlowDocumentUpload', clientOrganizationId, configurationId, userId, applicationId],
      });

      changeToNextDocument();
    } else {
      setCommand(undefined);
      setDocument(undefined);
    }
  };

  const refetchList = async () => {
    queryClient.invalidateQueries({
      queryKey: ['getUserFlow', clientOrganizationId, configurationId, userId, applicationId],
    });
    queryClient.invalidateQueries({
      queryKey: ['getUserOOFlowDocumentUpload', clientOrganizationId, configurationId, userId, applicationId],
    });
  };

  const closeDrawerAndRefresh = async () => {
    setCommand(undefined);
    setDocument(undefined);
    refetchList();
  };

  const documentStatus = selectedDocument.userDocument?.additionalData?.status as OnboardingDocumentStatus;
  const isReadOnly = document.userDocument?.isReadOnly;

  const handleImageControls = (property: 'zoom' | 'rotate', value: number) => {
    setImageControls((state: any) => ({ ...state, [property]: state[property] + value }));
  };

  const handleMouseDown = (e: any) => {
    setOffsetX(e.clientX - imageControls.x);
    setOffsetY(e.clientY - imageControls.y);
  };

  const handleMouseMove = (e: any) => {
    const x = e.clientX - offsetX;
    const y = e.clientY - offsetY;

    setImageControls((state: any) => ({ ...state, x, y }));
  };

  const zoomValue = () => {
    const steps: any = {
      '-7': 25,
      '-6': 33,
      '-5': 50,
      '-4': 67,
      '-3': 75,
      '-2': 80,
      '-1': 90,
      '0': 100,
      '1': 110,
      '2': 125,
      '3': 150,
      '4': 175,
      '5': 200,
      '6': 250,
      '7': 300,
      '8': 400,
      '9': 500,
    };

    if (imageControls.zoom > 9) {
      setImageControls((state) => ({ ...state, zoom: 9 }));
    }

    if (imageControls.zoom < -7) {
      setImageControls((state) => ({ ...state, zoom: -7 }));
    }

    return steps[imageControls.zoom.toString()] / 100;
  };

  const onSubmit = async (values: any, actions: any) => {
    try {
      actions.setSubmitting(true);
      const files: any = {};
      const data: any = { documentAction };
      const partsNames = selectedDocument.parts.map((part) => part.name);
      if (selectedDocument.controls.length > 0 || selectedDocument.reUpload) {
        for (const [name, value] of Object.entries(values)) {
          if (partsNames.includes(name)) {
            files[name] = state[name].file;
          } else {
            data[name] = value;
          }
        }
        if (!part?.name) {
          for (const part of selectedDocument.parts) {
            files[part.name] = images.find((i) => i?.name === part.name)?.file;
          }
        }
        OOFlowWrapper.prepareValues(selectedDocument.controls, data);
        const sendData = {
          currentStep: documentationModel?.uploadEarly ? OOStepsEnum.documentUploadTwo : stepName,
          currentPage: selectedDocument.name,
          data: data,
        };
        await mutateAsync({ data: sendData, files });
      }
      setStatusEx(documentAction);
    } catch (err) {
      const parsed = parseValidationErrors(t, err);
      if (parsed.errors) {
        actions.setErrors(parsed.errors);
      }
    } finally {
      actions.setSubmitting(false);
    }
  };

  const validateFile = (file: File) => {
    const sizeLimit = Number(uploadFileSizeInfo?.maxUploadedFileSizeMB);
    const sizeLimitToMB = sizeLimit ? sizeLimit * 1000000 : 0;
    const fileValidation = validateChosenFile(store.getState().tenant.tenantId, file, {
      allowOnlyImages: false,
      maxSize: sizeLimitToMB,
    });
    return fileValidation;
  };

  const onInvalidFileChoose = (validation: ChosenFileValidation) => {
    if (!validation.valid) {
      setTypeError(validation.typeError);
      setSizeError(validation.sizeError);
      setEmptyFileError(validation.emptyFileError);
    }
  };

  const handleUndoRejectedDocument = (props: any) => {
    if (props?.values?.[DOCUMENT_REJECTION_REASON]) {
      const newValues: any = {
        ...props.values,
        DOCUMENT_REJECTION_REASON: null,
      };
      props.setValues(newValues);
      onSubmit(newValues, props);
    } else {
      setStatusEx(documentAction);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.form}>
        <Formik
          validateOnMount={true}
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={validationSchema}
          validateOnChange={true}
          onSubmit={onSubmit}
        >
          {(props) => (
            <form onSubmit={props.handleSubmit} encType="multipart/form-data" style={{ flexGrow: 1 }}>
              {isReadOnly && (
                <ReadOnlyDocumentInfo
                  document={document}
                  setDocument={setDocument}
                  isRecruiter={true}
                  closeDrawer={closeDrawerAndRefresh}
                />
              )}
              {!isReadOnly && (
                <Grid container spacing={3}>
                  {selectedDocument.controls
                    .filter((c: OOControlModel) => c.isVisibleRecruiter)
                    .sort((a, b) => {
                      if (a.sequence && b.sequence) {
                        return a.sequence - b.sequence;
                      }
                      return 0;
                    })
                    .map((control: OOControlModel) => (
                      <Grid key={control.name} item xs={12}>
                        <OOFormControl control={control} />
                      </Grid>
                    ))}
                </Grid>
              )}
              <>
                {approvalInProgress || props.isSubmitting ? (
                  <Box style={{ textAlign: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box className="documents-and-data-upload-approve-modal__action-container">
                    <div className="tag-ds " style={{ width: '100%' }}>
                      {documentStatus === OnboardingDocumentStatus.REJECTED && hasValidationStep && (
                        <>
                          <Box mb={2}>
                            <div
                              className="tag-ds status-tag_error"
                              style={{ height: 40, borderRadius: 8, width: '100%' }}
                            >
                              {t('documents:statuses.status_rejected')}
                              <div className="material-icons" style={{ marginLeft: 10, color: '#E87A71' }}>
                                do_not_disturb_alt
                              </div>
                            </div>
                          </Box>
                          <button
                            type="button"
                            disabled={selectedOnboarding?.status === OnboardingStatus.hired}
                            onClick={() => {
                              documentAction = isReadOnly
                                ? OnboardingDocumentStatus.VALIDATING
                                : OnboardingDocumentStatus.REVIEW;
                              handleUndoRejectedDocument(props);
                            }}
                            style={{ width: '100%' }}
                          >
                            {t('candidate_recruiter:GENERAL.GENERIC.undo')}
                          </button>
                        </>
                      )}
                      {documentStatus === OnboardingDocumentStatus.VERIFIED && hasValidationStep && (
                        <>
                          <Box mb={2}>
                            <div
                              className="tag-ds status-tag_success "
                              style={{ height: 40, borderRadius: 8, width: '100%' }}
                            >
                              {t('documents:statuses.status_approved')}
                              <div className="material-icons" style={{ marginLeft: 10, color: '#73D85A' }}>
                                check_circle
                              </div>
                            </div>
                          </Box>
                          <button
                            disabled={
                              selectedOnboarding?.status === OnboardingStatus.hired ||
                              selectedOnboarding?.status === OnboardingStatus.readyForHire
                            }
                            onClick={() =>
                              setStatusEx(
                                isReadOnly ? OnboardingDocumentStatus.VALIDATING : OnboardingDocumentStatus.REVIEW,
                              )
                            }
                            style={{ width: '100%' }}
                          >
                            {t('candidate_recruiter:GENERAL.GENERIC.undo')}
                          </button>
                        </>
                      )}
                      {[OnboardingDocumentStatus.REVIEW, OnboardingDocumentStatus.VALIDATING].includes(
                        documentStatus,
                      ) &&
                        hasValidationStep && (
                          <>
                            {documentStatus !== OnboardingDocumentStatus.VALIDATING && (
                              <button
                                disabled={
                                  !props.isValid ||
                                  approvalInProgress ||
                                  props.isSubmitting ||
                                  !!props?.values?.[DOCUMENT_REJECTION_REASON]
                                }
                                onClick={() => {
                                  documentAction = OnboardingDocumentStatus.VERIFIED;
                                  props.handleSubmit();
                                }}
                                style={{ width: '100%', marginBottom: 16 }}
                              >
                                {t('candidate_recruiter:DOCUMENTS.DOCUMENT_UPLOAD.approveDocument')}
                              </button>
                            )}
                            <button
                              type="button"
                              disabled={
                                props.isSubmitting ||
                                (rejectionReasonControl && !props?.values?.[DOCUMENT_REJECTION_REASON])
                              }
                              onClick={() => {
                                documentAction = OnboardingDocumentStatus.REJECTED;
                                rejectionReasonControl ? onSubmit(props.values, props) : setStatusEx(documentAction);
                              }}
                              style={{ width: '100%' }}
                              className="button-secondary"
                            >
                              {t('candidate_recruiter:DOCUMENTS.DOCUMENT_UPLOAD.rejectDocument')}
                            </button>
                          </>
                        )}

                      {documentStatus === OnboardingDocumentStatus.VERIFIED &&
                        selectedDocument.reUpload &&
                        selectedDocument.parts.map((part: any) => (
                          <>
                            <Grid item sm={12} md={12} xs={12} key={part.name}>
                              <label
                                htmlFor={`btn-upload-${part.name}`}
                                className="document-form__action-button-special document-upload-label"
                              >
                                <FileUpload
                                  props={props}
                                  part={part}
                                  controls={selectedDocument.controls}
                                  onInvalidFileChoose={onInvalidFileChoose}
                                  validateFile={validateFile}
                                  setState={setState}
                                />

                                <span
                                  className="tag-ds button-secondary"
                                  onClick={() => setPart(part)}
                                  style={{
                                    width: '100%',
                                    color: themeAdditionalData?.smallTitle?.color,
                                  }}
                                >
                                  {t('candidate_recruiter:DOCUMENTS.DOCUMENT_UPLOAD.addFiles').toUpperCase()}
                                  <span className="material-icons right">file_upload</span>
                                </span>
                              </label>
                            </Grid>
                            <div className="tag-ds" style={{ marginTop: 41 }}>
                              <button
                                type="submit"
                                className=""
                                disabled={!props.isValid || !props.dirty}
                                style={{ width: '100%' }}
                              >
                                {t('candidate_recruiter:GENERAL.GENERIC.save')}
                              </button>
                            </div>
                          </>
                        ))}
                    </div>
                  </Box>
                )}
              </>
            </form>
          )}
        </Formik>
      </div>
      {images.length > 1 && (
        <div className={styles.selection}>
          <div>
            {images.map((image: any, index: number) => (
              <React.Fragment key={index}>
                {image.isImage ? (
                  <img
                    className={styles.selection__item}
                    src={image.imageUrl}
                    alt="Document"
                    onClick={() => setSelectedImage(image)}
                    style={{
                      borderColor: image.name === selectedImage?.name ? ' #33CDFF' : ' transparent',
                    }}
                  />
                ) : (
                  <div
                    className={styles.selection__item}
                    style={{
                      borderColor: image.name === selectedImage?.name ? ' #33CDFF' : ' transparent',
                      position: 'relative',
                      padding: '16px 12px',
                      cursor: 'pointer',
                    }}
                    onClick={() => setSelectedImage(image)}
                  >
                    <span className="tag-ds small">{`${t('documents:actions.page')} ${index + 1}`}</span>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
      <div className={styles.previewContainer}>
        <div className={styles.preview} ref={divRef}>
          {!selectedImage && <CircularProgress />}
          {selectedImage && selectedImage.isImage && !state[part?.name ?? 'default']?.file && (
            <img
              src={selectedImage.imageUrl}
              alt="Dialog"
              width="640px"
              height="auto"
              style={{
                position: 'absolute',
                left: `${imageControls.x}px`,
                top: `${imageControls.y}px`,
                transform: `translate(-50%, -50%) scale(${zoomValue()}) rotate(${imageControls.rotate}deg)`,
                cursor: 'grab',
              }}
              onDragStart={handleMouseDown}
              onDragEnd={handleMouseMove}
              draggable={true}
            />
          )}
          {part && state[part?.name]?.file && (
            <Grid
              item
              xs={12}
              style={{
                position: 'absolute',
                left: `${imageControls.x}px`,
                top: `${imageControls.y}px`,
                transform: `translate(-50%, -50%) scale(${zoomValue()}) rotate(${imageControls.rotate}deg)`,
                cursor: 'grab',
              }}
            >
              <Box className="document-button-preview__preview-container" style={{ backgroundColor: 'White' }}>
                <ImagePreview state={state} part={part} />
                <Box
                  className="document-button-preview__icon"
                  onClick={() => {
                    setState({
                      ...state,
                      [part.name]: { ...state[part.name], file: undefined, data: null },
                    });
                    setPart(undefined);
                  }}
                >
                  <CancelIcon />
                </Box>
              </Box>
            </Grid>
          )}
          {selectedImage && !selectedImage.isImage && (
            <iframe title="Document" style={{ width: '100%', height: '100%' }} src={selectedImage.imageUrl} />
          )}
        </div>
        {selectedImage && selectedImage.isImage && (
          <PreviewActions
            imageControls={imageControls}
            currentActiveImageIndex={currentActiveImageIndex}
            imagesLength={images.length}
            selectedImageName={t(selectedDocumentPartLabel)}
            handleImageControls={handleImageControls}
            zoomValue={zoomValue}
          />
        )}
      </div>
    </div>
  );
};

export default PreviewDocument;
